import { Stack, Text } from "@chakra-ui/react";
import React from "react";
import { useServices } from "services/useServices";
import { observer } from "mobx-react-lite";
import { AuthStore, CreateUserModel } from "./AuthStore";
import { Loading } from "components/Loading";
import { Button } from "components/Button";
import { Heading } from "components/Heading";
import { useBrand } from "brand/BrandConfig";
import { consumeAuthTokenFromURL, settleHandoffCode } from "services/coreapi";

interface AuthStoreProviderProps {
  /**
   * Builds the app's UserModel subclass. Must have a stable identity
   * (define it at module scope), or the store is recreated on re-render.
   */
  createUserModel?: CreateUserModel;
}

export const AuthStoreProvider = observer(
  ({
    children,
    createUserModel,
  }: React.PropsWithChildren<AuthStoreProviderProps>) => {
    const services = useServices();
    const brand = useBrand();
    const authStore = React.useMemo(() => {
      return new AuthStore(
        services.tracking,
        brand.ageConsentVersion,
        createUserModel,
      );
    }, [services, brand.ageConsentVersion, createUserModel]);

    React.useEffect(() => {
      // Normally already consumed in the app entry (ahead of Sentry); this
      // is the fallback for an entry that doesn't.
      consumeAuthTokenFromURL();
      // The swap must finish first, or the session check sees no token.
      void settleHandoffCode().then(() => authStore.init());
    }, [authStore]);

    // Couldn't find out whether there's a session: don't guess. Sending
    // the user to the login page would look like a logout; ask to retry
    // instead (and retry by itself when the connection returns).
    const couldNotCheck = authStore.initError !== null && !authStore.user;
    React.useEffect(() => {
      if (!couldNotCheck) return;
      const retry = () => authStore.init();
      window.addEventListener("online", retry);
      return () => window.removeEventListener("online", retry);
    }, [couldNotCheck, authStore]);

    if (authStore.isInitializing) {
      return (
        <Stack minH={"100vh"} alignItems={"center"} justifyContent={"center"}>
          <Loading />
        </Stack>
      );
    }

    if (couldNotCheck) {
      const offline =
        typeof navigator !== "undefined" && navigator.onLine === false;
      return (
        <Stack
          minH={"100vh"}
          alignItems={"center"}
          justifyContent={"center"}
          textAlign={"center"}
          gap={3}
          px={6}
        >
          <Heading fontSize={"xl"}>
            {offline ? "You seem to be offline" : "Something went wrong"}
          </Heading>
          <Text color="whiteAlpha.700" maxW={"320px"}>
            We couldn't check your sign-in. Check your connection and try again.
          </Text>
          <Button width="auto" mt={2} onClick={() => authStore.init()}>
            Try again
          </Button>
        </Stack>
      );
    }

    return (
      <AuthStoreContext.Provider value={authStore}>
        {children}
      </AuthStoreContext.Provider>
    );
  },
);

const AuthStoreContext = React.createContext<AuthStore | null>(null);

export function useAuthStore(): AuthStore {
  const store = React.useContext(AuthStoreContext);
  if (!store) {
    throw new Error("useAuthStore must be used within AuthStoreProvider");
  }
  return store;
}
